import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// ── Designs ───────────────────────────────────────────────────────────────────

const getDesigns = () =>
  axios.get(`${API_URL}/api/product-labels/designs`, authHeaders())
    .then(r => r.data.designs || []);

const createDesign = (data) =>
  axios.post(`${API_URL}/api/product-labels/designs`, data, authHeaders())
    .then(r => r.data.design);

const updateDesign = (id, data) =>
  axios.put(`${API_URL}/api/product-labels/designs/${id}`, data, authHeaders())
    .then(r => r.data.design);

const deleteDesign = (id) =>
  axios.delete(`${API_URL}/api/product-labels/designs/${id}`, authHeaders())
    .then(r => r.data);

// ── Label Templates ───────────────────────────────────────────────────────────

const getTemplates = () =>
  axios.get(`${API_URL}/api/product-labels`, authHeaders())
    .then(r => r.data.templates || []);

const createTemplate = (data) =>
  axios.post(`${API_URL}/api/product-labels`, data, authHeaders())
    .then(r => r.data.template);

const getTemplate = (id) =>
  axios.get(`${API_URL}/api/product-labels/${id}`, authHeaders())
    .then(r => r.data.template);

const updateTemplate = (id, data) =>
  axios.patch(`${API_URL}/api/product-labels/${id}`, data, authHeaders())
    .then(r => r.data.template);

const deleteTemplate = (id) =>
  axios.delete(`${API_URL}/api/product-labels/${id}`, authHeaders())
    .then(r => r.data);

// ── Versions ──────────────────────────────────────────────────────────────────

const createVersion = (templateId, data) =>
  axios.post(`${API_URL}/api/product-labels/${templateId}/versions`, data, authHeaders())
    .then(r => r.data.version);

const updateVersion = (templateId, versionId, data) =>
  axios.patch(`${API_URL}/api/product-labels/${templateId}/versions/${versionId}`, data, authHeaders())
    .then(r => r.data.version);

const deleteVersion = (templateId, versionId) =>
  axios.delete(`${API_URL}/api/product-labels/${templateId}/versions/${versionId}`, authHeaders())
    .then(r => r.data);

const submitVersion = (templateId, versionId) =>
  axios.post(`${API_URL}/api/product-labels/${templateId}/versions/${versionId}/submit`, {}, authHeaders())
    .then(r => r.data.version);

const approveVersion = (templateId, versionId) =>
  axios.post(`${API_URL}/api/product-labels/${templateId}/versions/${versionId}/approve`, {}, authHeaders())
    .then(r => r.data);

const rejectVersion = (templateId, versionId, reviewNotes) =>
  axios.post(`${API_URL}/api/product-labels/${templateId}/versions/${versionId}/reject`, { reviewNotes }, authHeaders())
    .then(r => r.data.version);

// ── Label Assets (image library) ──────────────────────────────────────────────

const getAssets = () =>
  axios.get(`${API_URL}/api/product-labels/assets`, authHeaders())
    .then(r => r.data.assets || []);

const getAssetUploadUrl = (fileName, contentType, fileSize) =>
  axios.post(`${API_URL}/api/product-labels/assets/upload-url`, { fileName, contentType, fileSize }, authHeaders())
    .then(r => r.data);

const createAsset = (data) =>
  axios.post(`${API_URL}/api/product-labels/assets`, data, authHeaders())
    .then(r => r.data.asset);

const deleteAsset = (id) =>
  axios.delete(`${API_URL}/api/product-labels/assets/${id}`, authHeaders())
    .then(r => r.data);

// ── Generate ingredients from recipe ─────────────────────────────────────────

const generateIngredients = (productId) =>
  axios.get(`${API_URL}/api/product-labels/generate-ingredients/${productId}`, authHeaders())
    .then(r => r.data);

// ── Lot codes for a product ───────────────────────────────────────────────────

const getLotCodes = (productId) =>
  axios.get(`${API_URL}/api/product-labels/lot-codes/${productId}`, authHeaders())
    .then(r => r.data.lots || []);

// ── Open orders for a product (print dialog's optional PO/order picker) ──────

const getOpenOrders = (productId) =>
  axios.get(`${API_URL}/api/product-labels/open-orders/${productId}`, authHeaders())
    .then(r => r.data.orders || []);

// Resolve the correct template for a product + labelType + (optional) customer —
// prefers a customer-specific template, falls back to the generic one.
const resolveTemplate = (productId, labelType, customerId) =>
  axios.get(`${API_URL}/api/product-labels/resolve/${productId}`, {
    ...authHeaders(),
    params: { labelType, ...(customerId ? { customerId } : {}) },
  }).then(r => r.data);

// ── Label Variants ────────────────────────────────────────────────────────────

const createVariant = (templateId, data) =>
  axios.post(`${API_URL}/api/product-labels/${templateId}/variants`, data, authHeaders())
    .then(r => r.data.variant);

const updateVariant = (templateId, variantId, data) =>
  axios.put(`${API_URL}/api/product-labels/${templateId}/variants/${variantId}`, data, authHeaders())
    .then(r => r.data.variant);

const deleteVariant = (templateId, variantId) =>
  axios.delete(`${API_URL}/api/product-labels/${templateId}/variants/${variantId}`, authHeaders())
    .then(r => r.data);

const getVariantsForImport = () =>
  axios.get(`${API_URL}/api/product-labels/variants-for-import`, authHeaders())
    .then(r => r.data.variants || []);

// ── Print ─────────────────────────────────────────────────────────────────────

const logPrintJob = (templateId, data) =>
  axios.post(`${API_URL}/api/product-labels/${templateId}/print`, data, authHeaders())
    .then(r => r.data.job);

const getPrintHistory = (params = {}) =>
  axios.get(`${API_URL}/api/product-labels/print-history`, { ...authHeaders(), params })
    .then(r => ({ jobs: r.data.jobs || [], total: r.data.total ?? (r.data.jobs || []).length }));

const productLabelService = {
  getDesigns, createDesign, updateDesign, deleteDesign,
  getTemplates, createTemplate, getTemplate, updateTemplate, deleteTemplate,
  createVersion, updateVersion, deleteVersion, submitVersion, approveVersion, rejectVersion,
  getAssets, getAssetUploadUrl, createAsset, deleteAsset,
  generateIngredients,
  getLotCodes,
  getOpenOrders, resolveTemplate,
  createVariant, updateVariant, deleteVariant, getVariantsForImport,
  logPrintJob, getPrintHistory,
};

export default productLabelService;
